import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingCart, User, LogOut, ShieldCheck, Menu } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobile, setMobile] = React.useState(false);

  const links = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Catalog" },
    { to: "/about", label: "About" },
    { to: "/qr", label: "Scan" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-slate-950/70 backdrop-blur-xl border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 sm:h-28 flex items-center justify-between">
        <Link to="/" className="flex items-center group -my-2" data-testid="logo-link">
          <img
            src="/logo-transparent.png"
            alt="Novagenix Labs"
            className="h-24 sm:h-32 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-base font-medium tracking-wide transition-colors ${isActive ? "text-cyan-400" : "text-slate-300 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="admin-link" className="hidden sm:inline-flex items-center gap-1.5 text-xs label-mono text-cyan-400 border border-cyan-500/30 px-3 py-1.5 hover:bg-cyan-500/10 transition">
              <ShieldCheck size={14} /> Admin
            </Link>
          )}
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/account" data-testid="account-link" className="text-sm text-slate-300 hover:text-white px-3 py-1.5 flex items-center gap-1.5">
                <User size={16} /> {user.name.split(" ")[0]}
              </Link>
              <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-cyan-400 p-2">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="hidden sm:inline-block text-sm text-slate-300 hover:text-white px-3 py-1.5">
              Sign in
            </Link>
          )}

          <button
            data-testid="cart-toggle"
            onClick={() => setOpen(true)}
            className="relative inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-4 py-2 text-sm transition-colors"
          >
            <ShoppingCart size={16} />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count" className="ml-1 bg-slate-950 text-cyan-400 text-[10px] font-mono px-1.5 py-0.5">
                {count}
              </span>
            )}
          </button>

          <button onClick={() => setMobile((v) => !v)} className="md:hidden p-2 text-slate-300" data-testid="mobile-menu-toggle">
            <Menu size={18} />
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden border-t border-white/5 bg-slate-950 px-6 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setMobile(false)} className="text-sm text-slate-300 py-1">{l.label}</NavLink>
          ))}
          {user ? (
            <>
              <Link to="/account" onClick={() => setMobile(false)} className="text-sm text-slate-300 py-1">Account</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setMobile(false)} className="text-sm text-cyan-400 py-1">Admin</Link>}
              <button onClick={() => { logout(); setMobile(false); }} className="text-left text-sm text-slate-300 py-1">Sign out</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMobile(false)} className="text-sm text-cyan-400 py-1">Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}
